import { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { previewBdcAdjust, saveBdcDelivery } from "../../../services";
import { BdcAdjustPreview } from "../../../types/bdc.type";
import { readBdcError } from "../apiError";
import { lineText, pcs } from "../format";
import { SURFACE } from "../../common/designTokens";
import { checkNewTotal, DeliveryRow, previewToItems } from "./delivery";

/** Preview rows drawn before "… อีก n ร้าน". */
const PREVIEW_ROWS_SHOWN = 50;

function Figure({ label, value, warn }: Readonly<{ label: string; value: number; warn?: boolean }>) {
  return (
    <Box sx={{ bgcolor: warn ? "#FFF0D6" : "#F5F5F6", borderRadius: "6px", p: 1 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography sx={{ fontSize: "1rem", fontWeight: 600 }}>{pcs(value)}</Typography>
    </Box>
  );
}

/**
 * เกลี่ยยอดอัตโนมัติ — one product of a draft PO (Shipment board, right panel). The user types the
 * new delivered total, the server proposes the cut (rule ข้อ 4: biggest store first, one piece at a
 * time, everybody keeps 1); when keeping 1 cannot reach the total it asks before any store goes
 * to 0. Nothing is written until บันทึกยอดส่ง (PUT …/delivery, mode auto).
 */
export default function AutoAdjustPanel({
  poNumber,
  row,
  onClose,
  onSaved,
}: Readonly<{
  poNumber: string;
  row: DeliveryRow;
  onClose: () => void;
  onSaved: () => void;
}>) {
  const [text, setText] = useState("");
  const [preview, setPreview] = useState<BdcAdjustPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<"preview" | "save" | null>(null);

  useEffect(() => {
    setText("");
    setPreview(null);
    setError(null);
  }, [row.barcode]);

  const check = checkNewTotal(text, row.delivered);
  const typedError = text.trim() !== "" && "error" in check ? check.error : null;

  const runPreview = async (newTotal: number, allowZero: boolean) => {
    setBusy("preview");
    setError(null);
    try {
      const {
        data: { data },
      } = await previewBdcAdjust(poNumber, { barcode: row.barcode, new_total: newTotal, allow_zero: allowZero });
      setPreview(data);
    } catch (e) {
      setPreview(null);
      setError(readBdcError(e, "ดูตัวอย่างการเกลี่ยไม่สำเร็จ").message);
    } finally {
      setBusy(null);
    }
  };

  const save = async () => {
    if (!preview || preview.rows.length === 0) return;
    setBusy("save");
    try {
      await saveBdcDelivery(poNumber, { mode: "auto", items: previewToItems(preview) });
      onSaved();
    } catch {
      // the interceptor shows the server's reason
    } finally {
      setBusy(null);
    }
  };

  const typedValue = "value" in check ? check.value : null;
  const stale = preview !== null && typedValue !== preview.new_total;
  const canSave = preview !== null && !stale && !preview.requires_allow_zero && preview.rows.length > 0;

  return (
    <Box
      data-testid="bdc-auto-adjust"
      sx={{
        bgcolor: SURFACE.card,
        border: "1.5px solid #363A44",
        borderRadius: "10px",
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
      }}
    >
      <Typography sx={{ fontSize: "0.95rem", fontWeight: 600 }}>เกลี่ยยอดอัตโนมัติ · {row.name}</Typography>
      <Typography variant="body2" color="text.secondary">
        ลดได้อย่างเดียว · ตัดร้านที่ยอดมากสุดก่อนทีละชิ้น · ทุกร้านเหลืออย่างน้อย 1
      </Typography>
      <Stack direction="row" gap={1.25} alignItems="flex-start" flexWrap="wrap">
        <TextField
          size="small"
          label="ยอดส่งรวมใหม่"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && typedValue !== null) runPreview(typedValue, false);
          }}
          error={Boolean(typedError)}
          helperText={typedError ?? " "}
          sx={{ width: 160 }}
          slotProps={{ htmlInput: { inputMode: "numeric", "aria-label": "ยอดส่งรวมใหม่" } }}
        />
        <Typography variant="body2" sx={{ pt: 1 }}>
          จาก {pcs(row.delivered)}
          {typedValue !== null && ` (−${pcs(row.delivered - typedValue)})`}
          {row.delivered !== row.po && <span className="sub"> · PO {pcs(row.po)}</span>}
        </Typography>
        <Button
          variant="outlined"
          sx={{ mt: 0.25 }}
          disabled={typedValue === null}
          loading={busy === "preview"}
          onClick={() => typedValue !== null && runPreview(typedValue, false)}
        >
          ดูตัวอย่าง
        </Button>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      {preview?.requires_allow_zero && !stale && (
        <Alert severity="warning" data-testid="bdc-allow-zero">
          ให้ทุกร้านเหลืออย่างน้อย 1 ชิ้นแล้วยังลดไม่ถึง {pcs(preview.new_total)} — ต่ำสุดที่ทุกร้านยังได้ของคือ{" "}
          <b>{pcs(preview.min_total_keeping_one)}</b>
          <Stack direction="row" gap={1} mt={1} flexWrap="wrap">
            <Button size="small" variant="contained" color="warning" onClick={() => runPreview(preview.new_total, true)}>
              ยอมให้บางร้านเหลือ 0
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setText(String(preview.min_total_keeping_one));
                runPreview(preview.min_total_keeping_one, false);
              }}
            >
              ใช้ {pcs(preview.min_total_keeping_one)} แทน
            </Button>
          </Stack>
        </Alert>
      )}

      {preview && !preview.requires_allow_zero && (
        <>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 0.75 }}>
            <Figure label="ร้านที่สั่ง" value={preview.ordering_store_count} />
            <Figure label="โดนตัด" value={preview.cut_store_count} warn />
            <Figure label="เหลือ 0" value={preview.zero_store_count} warn={preview.zero_store_count > 0} />
          </Box>
          {stale && <Alert severity="info">ยอดที่กรอกเปลี่ยนแล้ว — กด ดูตัวอย่าง อีกครั้งก่อนบันทึก</Alert>}
          <Box sx={{ maxHeight: 320, overflow: "auto" }}>
            <table className="bdc-t">
              <thead>
                <tr>
                  <th>ร้าน</th>
                  <th>สาย/จุด</th>
                  <th className="n">PO</th>
                  <th className="n">ก่อน</th>
                  <th className="n">หลัง</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.slice(0, PREVIEW_ROWS_SHOWN).map((r) => (
                  <tr key={r.store_code}>
                    <td>
                      <span className="mono">{r.store_code}</span> {r.store_name}
                    </td>
                    <td>
                      {lineText(r.line_no)} / {lineText(r.stop_no)}
                    </td>
                    <td className="n sub">{pcs(r.po_qty)}</td>
                    <td className="n">{pcs(r.before)}</td>
                    <td className="n">
                      <b>{pcs(r.after)}</b>
                    </td>
                  </tr>
                ))}
                {preview.rows.length > PREVIEW_ROWS_SHOWN && (
                  <tr>
                    <td colSpan={5} className="sub">
                      … อีก {pcs(preview.rows.length - PREVIEW_ROWS_SHOWN)} ร้าน
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Box>
        </>
      )}

      <Stack direction="row" justifyContent="flex-end" gap={1}>
        <Button variant="outlined" onClick={onClose}>
          ยกเลิก
        </Button>
        <Button variant="contained" disabled={!canSave} loading={busy === "save"} onClick={save}>
          บันทึกยอดส่ง
        </Button>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        บันทึกแล้วยังแก้ต่อรายร้านได้ในแท็บ ใบคุมรายร้าน จนกว่าจะยืนยัน DO
      </Typography>
    </Box>
  );
}
